import { useCallback, useRef } from 'react'
import config from '@src/config'
import { useFormContext } from '@context/FormContext'

const clearFollowingValues = ({ prevValues, nextValues }) => {
  if (!prevValues || !nextValues) return []
  
  // Find which fields have changed
  const changedFields = []
  Object.keys(nextValues).forEach((key) => {
    if (prevValues[key] !== nextValues[key]) {
      changedFields.push(key)
    }
  })
  
  // If no fields changed, nothing to clear
  if (changedFields.length === 0) return []
  
  // Only clear fields that come AFTER the changed field in the form flow
  // Define the order of fields in the form flow
  const fieldOrder = [
    'path',
    'salesOrService',
    'twoYears',
    'paymentAssistance',
    'singleOrMulti',
    'ownerOrRenter',
    'isCondo',
    'familySize',
    'unitNumber1',
    'unitNumber2',
    'unitNumber3',
    'unitNumber4',
    'howHeated',
    'gasProvider',
    'electricProvider',
    'serviceReason',
    'systemToFix',
    'fixNotes',
    'systemToService',
    'systemServiceAge',
    'lastServiced',
    'fuelAssistance',
    'reasonHere',
    'existingDuctWork',
    'rentOrOwn',
    'homeAge',
    'contactInfo',
    'schedule',
  ]
  
  // Find the last changed field's position in the order
  let lastChangedIndex = -1
  changedFields.forEach((field) => {
    const index = fieldOrder.indexOf(field)
    if (index > lastChangedIndex) {
      lastChangedIndex = index
    }
  })
  
  // If the changed field is not in the order, don't clear anything
  if (lastChangedIndex < 0) return []
  
  // Get all fields that come after the changed field
  const fieldsToClear = fieldOrder.slice(lastChangedIndex + 1)
  
  // Filter out exempted fields and only return fields that actually exist in nextValues
  return fieldsToClear.filter((fieldName) => {
    // Don't clear if exempted
    if (config.clearFollowingValuesExempt?.includes(fieldName)) return false
    // Only clear if the field exists and has a value
    return nextValues[fieldName] !== undefined && nextValues[fieldName] !== null
  })
}

// Hook version that uses context
export const useClearFollowingValues = () => {
  const { setFieldValue } = useFormContext()
  const clearingRef = useRef(false)
  
  return useCallback(({ prevValues, nextValues }) => {
    // Prevent infinite loops by checking if we're already clearing
    if (clearingRef.current) return
    if (!prevValues || !nextValues) return
    
    const fieldsToClear = clearFollowingValues({ prevValues, nextValues })
    if (fieldsToClear && fieldsToClear.length > 0) {
      clearingRef.current = true
      // Only clear fields that actually have values (avoid clearing already-cleared fields)
      fieldsToClear.forEach((fieldName) => {
        const currentValue = nextValues[fieldName]
        if (currentValue !== undefined && currentValue !== null) {
          setFieldValue(fieldName, undefined)
        }
      })
      // Reset flag after a brief delay to allow state updates to complete
      setTimeout(() => {
        clearingRef.current = false
      }, 0)
    }
  }, [setFieldValue])
}

export default clearFollowingValues
